import { api } from "./client";
import { characterApi } from "./studioCharacter";
import type {
  AcceptIdentityInput,
  ActivityFilters,
  AuditFilters,
  CharacterFilters,
  CharacterJobFilters,
  CharacterMediaFilters,
  ConfirmProfileInput,
  DraftFilters,
  DraftPatch,
  RejectIdentityInput,
} from "./studioInputs";
import {
  characterRoot,
  draftRoot,
  orgRoot,
  paidHeaders,
  queryString,
  segment,
} from "./studioRoutes";
import type {
  AuditListResponse,
  ActivityListResponse,
  CharacterDetail,
  CharacterListResponse,
  CharacterVersion,
  DraftDetail,
  DraftListResponse,
  EntryMode,
  IdentityCandidate,
  Inspiration,
  InspirationProvenance,
  InspirationRightsInput,
  InspirationAnalysis,
  InspirationRole,
  Job,
  PaidPreview,
  PaidIntentResolutionResponse,
} from "@/lib/studio/types";
import type { VoiceProposal } from "@/lib/studio/voice";

/** One If-Match draft mutation, answered with the draft it leaves. */
function postForDraft(path: string, etag: string, body: unknown = {}) {
  return api<{ draft: DraftDetail }>(path, {
    method: "POST",
    headers: { "If-Match": etag },
    body,
  });
}

export class InspirationLimitError extends Error {
  constructor() {
    super("A character can have at most four inspiration images.");
    this.name = "InspirationLimitError";
  }
}

/**
 * Every organization-scoped cache key, built in one place.
 *
 * The slug sits at index 1 in all of them, and that position is load-bearing:
 * `dropForeignWorkspaceQueries` evicts one client's cached reads by matching
 * exactly there when the reader switches to another. A key assembled by hand
 * at a call site can omit the slug entirely, and one that does serves the
 * previous client's list under the next client's URL — which is the single
 * disclosure this product cannot survive.
 */
export const STUDIO_QK = {
  /** Prefix over every page of the authenticated actor's current-org Activity. */
  activity: (slug: string) => ["activity", slug] as const,
  activityList: (slug: string, filters: ActivityFilters) =>
    ["activity", slug, filters] as const,
  /** Prefix over every accepted-roster list and picker, for invalidation. */
  characters: (slug: string) => ["characters", slug] as const,
  characterList: (slug: string, filters: CharacterFilters) =>
    ["characters", slug, filters] as const,
  /** Prefix over every open-draft list, for invalidation. */
  drafts: (slug: string) => ["drafts", slug] as const,
  draftList: (slug: string, filters: DraftFilters) =>
    ["drafts", slug, filters] as const,
  /** `purpose` keeps two pickers of the same endpoint from sharing a cache. */
  allCharacters: (
    slug: string,
    purpose: string,
    filters: Omit<CharacterFilters, "cursor" | "limit">,
  ) => ["characters", slug, purpose, filters] as const,
  character: (slug: string, characterID: string) =>
    ["character", slug, characterID] as const,
  /** Optional live usable speaking-orbit frames; never part of character detail. */
  anchorCoverage: (slug: string, characterID: string) =>
    ["anchor-coverage", slug, characterID] as const,
  draft: (slug: string, draftID: string) => ["draft", slug, draftID] as const,
  /** Prefix over every individual accepted or archived character detail. */
  charactersDetail: (slug: string) => ["character", slug] as const,
  /**
   * The build preview held per draft rather than per revision: autosave
   * moves the ETag constantly and the resolved intent does not, and whether a
   * held preview may still be used is decided when it is submitted, not by
   * this key.
   */
  buildPreview: (slug: string, draftID: string) =>
    ["build-preview", slug, draftID] as const,
  identityCandidates: (slug: string, draftID: string) =>
    ["identity-candidates", slug, draftID] as const,
  /** The live profile and voice vocabulary, read from charactersd. */
  profileVocabulary: (slug: string) => ["profile-vocabulary", slug] as const,
  /** The workspace's curated video templates. */
  videoTemplates: (slug: string) => ["video-templates", slug] as const,
  /** Prefix over one character's library pages, for post-delete refresh. */
  characterMediaLists: (slug: string, characterID: string) =>
    ["character-media", slug, characterID] as const,
  characterMedia: (
    slug: string,
    characterID: string,
    filters: CharacterMediaFilters,
  ) => ["character-media", slug, characterID, filters] as const,
  /** Prefix over every page of one character's all-actor job history. */
  characterJobs: (slug: string, characterID: string) =>
    ["character-jobs", slug, characterID] as const,
  characterJobList: (
    slug: string,
    characterID: string,
    filters: CharacterJobFilters,
  ) => ["character-jobs", slug, characterID, filters] as const,
  /** Prefix over every character's all-actor job pages in one organization. */
  allCharacterJobs: (slug: string) => ["character-jobs", slug] as const,
  /** Prefix over every page of one character's curated history. */
  characterActivity: (slug: string, characterID: string) =>
    ["character-activity", slug, characterID] as const,
  characterActivityList: (
    slug: string,
    characterID: string,
    filters: ActivityFilters,
  ) => ["character-activity", slug, characterID, filters] as const,
  /** Prefix over every character's curated history pages in one organization. */
  allCharacterActivity: (slug: string) => ["character-activity", slug] as const,
  job: (slug: string, jobID: string) => ["job", slug, jobID] as const,
  audit: (slug: string, filters: AuditFilters) =>
    ["audit", slug, filters] as const,
};

/**
 * The address of one organization's media bytes, for `src` attributes.
 *
 * It lives here because every other `/v1` address does. A path assembled in a
 * component is a path that escapes `api()`'s same-origin guard and the one
 * encoding rule that keeps a slug or id with a slash in it from silently
 * addressing a different organization's row.
 *
 * The browser sends the session cookie on the resulting request, so
 * authorization is the server's as usual — there is no token in this URL and
 * there must never be one.
 */
export function mediaUrl(
  orgSlug: string,
  mediaID: string,
  variant: "content" | "thumbnail",
): string {
  return `${orgRoot(orgSlug)}/media/${segment(mediaID)}/${variant}`;
}

/** Organization-authorized draft inspiration bytes; never an upstream URL. */
export function inspirationUrl(
  orgSlug: string,
  draftID: string,
  inspirationID: string,
  variant: "content" | "thumbnail",
): string {
  return `${draftRoot(orgSlug, draftID)}/inspirations/${segment(inspirationID)}/${variant}`;
}

export const studioApi = {
  ...characterApi,

  listCharacters: (orgSlug: string, filters: CharacterFilters) =>
    api<CharacterListResponse>(
      `${orgRoot(orgSlug)}/characters${queryString({ ...filters })}`,
    ),

  getCharacter: (orgSlug: string, characterID: string) =>
    api<{ character: CharacterDetail }>(characterRoot(orgSlug, characterID)),

  /**
   * A new editable draft from an accepted or archived character. The source
   * is never changed; the draft has its own UUID and needs fresh confirmation.
   */
  duplicateCharacter: (orgSlug: string, characterID: string, etag: string) =>
    postForDraft(`${characterRoot(orgSlug, characterID)}/duplicate`, etag),

  listDrafts: (orgSlug: string, filters: DraftFilters) =>
    api<DraftListResponse>(
      `${orgRoot(orgSlug)}/drafts${queryString({ ...filters })}`,
    ),

  getDraft: (orgSlug: string, draftID: string) =>
    api<{ draft: DraftDetail }>(draftRoot(orgSlug, draftID)),

  createDraft: (
    orgSlug: string,
    input: {
      entry_mode: EntryMode;
      name: string;
      fictional_age: number | null;
      text_prompt?: string;
    },
  ) =>
    api<{ draft: DraftDetail }>(`${orgRoot(orgSlug)}/drafts`, {
      method: "POST",
      body: input,
    }),

  patchDraft: (
    orgSlug: string,
    draftID: string,
    etag: string,
    input: DraftPatch,
  ) =>
    api<{ draft: DraftDetail }>(draftRoot(orgSlug, draftID), {
      method: "PATCH",
      headers: { "If-Match": etag },
      body: input,
    }),

  discardDraft: (orgSlug: string, draftID: string, etag: string) =>
    api<void>(draftRoot(orgSlug, draftID), {
      method: "DELETE",
      headers: { "If-Match": etag },
    }),

  uploadInspiration: (
    orgSlug: string,
    draftID: string,
    etag: string,
    existingCount: number,
    input: {
      file: File;
      slot: number;
      role: InspirationRole;
      provenance: InspirationProvenance;
      rights_context: InspirationRightsInput;
    },
  ) => {
    if (existingCount >= 4) return Promise.reject(new InspirationLimitError());
    const formData = new FormData();
    formData.set("file", input.file);
    formData.set("slot", String(input.slot));
    formData.set("role", input.role);
    formData.set("provenance", JSON.stringify(input.provenance));
    formData.set("rights_context", JSON.stringify(input.rights_context));
    return api<{ inspiration: Inspiration; character_etag: string }>(
      `${draftRoot(orgSlug, draftID)}/inspirations`,
      {
        method: "POST",
        headers: { "If-Match": etag },
        formData,
      },
    );
  },

  removeInspiration: (
    orgSlug: string,
    draftID: string,
    inspirationID: string,
    etag: string,
  ) =>
    api<{ character_etag: string }>(
      `${draftRoot(orgSlug, draftID)}/inspirations/${segment(inspirationID)}`,
      { method: "DELETE", headers: { "If-Match": etag } },
    ),

  analyzeInspirations: (orgSlug: string, draftID: string, etag: string) =>
    api<{
      draft: DraftDetail;
      analysis: InspirationAnalysis;
      version: CharacterVersion;
    }>(`${draftRoot(orgSlug, draftID)}/inspiration-analysis`, {
      method: "POST",
      headers: { "If-Match": etag },
    }),

  /** Freezes the saved profile and the exact voice the member was shown. */
  confirmProfile: (
    orgSlug: string,
    draftID: string,
    etag: string,
    input: ConfirmProfileInput,
  ) =>
    postForDraft(`${draftRoot(orgSlug, draftID)}/confirm-profile`, etag, input),

  /**
   * One free Voice DNA proposal for the observed revision. Nothing is saved:
   * the caller applies it only while both returned ETags are still current.
   */
  voiceProposal: (
    orgSlug: string,
    draftID: string,
    etag: string,
    input: { version_etag: string },
  ) =>
    api<VoiceProposal>(`${draftRoot(orgSlug, draftID)}/voice-proposal`, {
      method: "POST",
      headers: { "If-Match": etag },
      body: input,
    }),

  /**
   * A new attempt from the newest failed or rejected one: a new version and a
   * new upstream target. The rendered attempt it follows is never rewritten.
   */
  retryDraft: (orgSlug: string, draftID: string, etag: string) =>
    postForDraft(`${draftRoot(orgSlug, draftID)}/versions`, etag),

  /** Hides a resolved draft without deleting its history. */
  abandonDraft: (orgSlug: string, draftID: string, etag: string) =>
    postForDraft(`${draftRoot(orgSlug, draftID)}/abandon`, etag),

  previewBuild: (orgSlug: string, draftID: string, etag: string) =>
    api<{ preview: PaidPreview }>(
      `${draftRoot(orgSlug, draftID)}/build-previews`,
      { method: "POST", headers: { "If-Match": etag }, body: {} },
    ),

  submitBuild: (
    orgSlug: string,
    draftID: string,
    etag: string,
    idempotencyKey: string,
    input: {
      version_id: string;
      preview_id: string;
    },
  ) =>
    api<{ job: Job }>(`${draftRoot(orgSlug, draftID)}/builds`, {
      method: "POST",
      headers: paidHeaders(etag, idempotencyKey),
      body: input,
    }),

  listCandidates: (orgSlug: string, draftID: string) =>
    api<{ candidates: IdentityCandidate[] }>(
      `${draftRoot(orgSlug, draftID)}/identity-candidates`,
    ),

  /** Promotes the same UUID to an accepted character. */
  acceptIdentity: (
    orgSlug: string,
    draftID: string,
    etag: string,
    input: AcceptIdentityInput,
  ) =>
    api<{ character: CharacterDetail }>(
      `${draftRoot(orgSlug, draftID)}/identity-review`,
      {
        method: "PUT",
        headers: { "If-Match": etag },
        body: { ...input, decision: "accepted" },
      },
    ),

  /** Leaves a draft whose rejected attempt stays frozen until a retry. */
  rejectIdentity: (
    orgSlug: string,
    draftID: string,
    etag: string,
    input: RejectIdentityInput,
  ) =>
    api<{ draft: DraftDetail }>(
      `${draftRoot(orgSlug, draftID)}/identity-review`,
      {
        method: "PUT",
        headers: { "If-Match": etag },
        body: { ...input, decision: "rejected" },
      },
    ),

  archiveCharacter: (orgSlug: string, characterID: string, etag: string) =>
    api<{ character: CharacterDetail }>(
      `${characterRoot(orgSlug, characterID)}/archive`,
      { method: "POST", headers: { "If-Match": etag }, body: {} },
    ),

  restoreCharacter: (orgSlug: string, characterID: string, etag: string) =>
    api<{ character: CharacterDetail }>(
      `${characterRoot(orgSlug, characterID)}/restore`,
      { method: "POST", headers: { "If-Match": etag }, body: {} },
    ),

  listActivity: (orgSlug: string, filters: ActivityFilters = {}) =>
    api<ActivityListResponse>(
      `${orgRoot(orgSlug)}/jobs${queryString({
        view: "activity",
        limit: filters.limit,
        cursor: filters.cursor,
      })}`,
    ),
  markActivitySeen: (orgSlug: string, jobIDs: readonly string[]) =>
    api<void>(`${orgRoot(orgSlug)}/activity/seen`, {
      method: "POST",
      body: { job_ids: jobIDs },
    }),
  acknowledgeActivity: (orgSlug: string, jobID: string, etag: string) =>
    api<void>(`${orgRoot(orgSlug)}/jobs/${segment(jobID)}/acknowledge`, {
      method: "POST",
      headers: { "If-Match": etag },
      body: {},
    }),

  resolvePaidIntents: (orgSlug: string, keys: readonly string[]) =>
    api<PaidIntentResolutionResponse>(
      `${orgRoot(orgSlug)}/paid-intents/resolve`,
      { method: "POST", body: { keys } },
    ),
  getJob: (orgSlug: string, jobID: string) =>
    api<{ job: Job }>(`${orgRoot(orgSlug)}/jobs/${segment(jobID)}`),
  resumeJob: (orgSlug: string, jobID: string, etag: string, prompt?: string) =>
    api<{ job: Job }>(`${orgRoot(orgSlug)}/jobs/${segment(jobID)}/resume`, {
      method: "POST",
      headers: { "If-Match": etag },
      body: prompt === undefined ? {} : { prompt },
    }),
  abandonJob: (orgSlug: string, jobID: string, etag: string) =>
    api<{ job: Job }>(`${orgRoot(orgSlug)}/jobs/${segment(jobID)}/abandon`, {
      method: "POST",
      headers: { "If-Match": etag },
      body: {},
    }),
  listAudit: (orgSlug: string, filters: AuditFilters) =>
    api<AuditListResponse>(
      `${orgRoot(orgSlug)}/audit${queryString({ ...filters })}`,
    ),
};
